import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';

import { useCanUsePaymentRequestButtonMethods } from 'src/app/shared/components/payments';
import { useLoginState } from 'src/app/shared/hooks/session';
import { useIsMobile } from 'src/app/shared/hooks/userAgent';
import type { PaymentType } from 'src/app/shared/utils/payments';

import { usePromotePageData } from '../pageData';

import { AvailablePaymentMethodsContext } from './context';

export function AvailablePaymentMethodsProvider({ children }: PropsWithChildren<EmptyProps>): JSX.Element {
	const {
		data: {
			featureConfigs: { paymentMethodsEnabledFor, paypalMobileDefault },
		},
	} = usePromotePageData();
	const isMobile = useIsMobile();
	const canUsePaymentRequestButtonMethods = useCanUsePaymentRequestButtonMethods();
	const loginState = useLoginState();

	const paymentMethods = useMemo(() => {
		const availablePaymentMethods: PaymentType[] = [];
		if (!paymentMethodsEnabledFor) {
			availablePaymentMethods.push('creditCard');
			return availablePaymentMethods;
		}
		paymentMethodsEnabledFor.forEach((paymentType) => {
			switch (paymentType.payment_method_name) {
				case 'paypal':
					availablePaymentMethods[isMobile && paypalMobileDefault ? 'unshift' : 'push']('paypal');
					break;
				case 'applePay':
				case 'googlePay':
					if (canUsePaymentRequestButtonMethods[paymentType.payment_method_name])
						availablePaymentMethods.push(paymentType.payment_method_name);
					break;
				case 'sepa':
					// "email" is required for sepa, but we are not able to retrieve that value for identified users.
					// this payment method will be hidden for identified users.
					if (loginState !== 'IDENTIFIED') availablePaymentMethods.push('sepa');
					break;
				default:
					availablePaymentMethods.push(paymentType.payment_method_name);
			}
		});
		return availablePaymentMethods;
	}, [canUsePaymentRequestButtonMethods, isMobile, paymentMethodsEnabledFor, paypalMobileDefault, loginState]);

	return (
		<AvailablePaymentMethodsContext.Provider
			value={{
				paymentMethods,
			}}
		>
			{children}
		</AvailablePaymentMethodsContext.Provider>
	);
}
